import { Entity, isUserEntity } from '@backstage/catalog-model';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
} from '@backstage/plugin-catalog';

/**
 * The stock "Name" column searches `resolved.entityRef` — a compact string
 * like `user:default/poya-sedighi` — via Material-Table's own un-tokenized,
 * whole-string substring search. A query containing a space (a real full
 * name) can never match that ref, since it has no spaces (see PAT-914).
 * This replicates the multi-word, multi-field matching Backstage's own
 * `EntityTextFilter` already implements, but that this app never wires into
 * what the table actually searches.
 */
export function matchesFullNameQuery(entity: Entity, query: string): boolean {
  const words = query
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.toLocaleUpperCase('en-US'));
  if (words.length === 0) {
    return true;
  }

  const searchableValues = [
    entity.metadata.name,
    entity.metadata.title,
    isUserEntity(entity) ? entity.spec.profile?.displayName : undefined,
  ]
    .filter((value): value is string => typeof value === 'string')
    .map(value => value.toLocaleUpperCase('en-US'));

  return words.every(word =>
    searchableValues.some(value => value.includes(word)),
  );
}

/**
 * Wraps Backstage's stock catalog table columns, overriding only the Name
 * column's search behavior for the User-kind view (per PAT-914's scope).
 * Every other kind, and every other column, is left exactly as stock.
 */
export const userCatalogColumns: CatalogTableColumnsFunc =
  entityListContext => {
    const columns = CatalogTable.defaultColumnsFunc(entityListContext);
    const kind =
      entityListContext.filters.kind?.value?.toLocaleLowerCase('en-US');
    if (kind !== 'user') {
      return columns;
    }

    return columns.map(column =>
      column.field === 'resolved.entityRef'
        ? {
            ...column,
            customFilterAndSearch: (query: string, row: { entity: Entity }) =>
              matchesFullNameQuery(row.entity, query),
          }
        : column,
    );
  };
